import { getDeviceLocale } from "@cabocheck/shared";
import type { LoaderFunctionArgs } from "react-router";
import { useLoaderData } from "react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import benefit1 from "~/assets/images/microsite/apotheke_benefit_1.png";
import benefit3 from "~/assets/images/microsite/apotheke_benefit_3.png";
import benefitHero2 from "~/assets/images/microsite/aptheke_benefit_hero_2.png";
import benefitHero3 from "~/assets/images/microsite/aptheke_benefit_hero_3.png";
import benefitHero4 from "~/assets/images/microsite/microsite_benefit_hero_4.jpg";
import benefitHero1 from "~/assets/images/microsite/benefit_hero_1.png";
import apothekeIcon from "~/assets/apotheke.png";
import no_test_kit from "~/assets/no_test_kit.png";
import customer_1 from "~/assets/images/microsite/customer_1.jpeg";
import onboarding_1 from "~/assets/images/microsite/onboarding_1.jpg";
import onboarding_2 from "~/assets/images/microsite/onboarding_2.jpg";
import onboarding_3 from "~/assets/images/microsite/onboarding_3.webp";
import blue_1 from "~/assets/images/microsite/blue_1.jpg";
import blue_2 from "~/assets/images/microsite/blue_2.jpg";
import blue_3 from "~/assets/images/microsite/blue_3.jpg";
import landing_box from "~/assets/landing_box.webp";
import ContactBannerImage from "~/assets/images/cabopoc_contact_banner.jpeg";
import { ContactBanner } from "~/features/microsite/ContactBanner";
import { FAQComponent } from "~/features/microsite/FAQs";
import { TestOffersComponent } from "~/features/microsite/TestOffers";
import { PublicFooter } from "~/features/public/Footer";
import { MedicalTestProfileBox } from "~/features/tests/MedicalTestProfileBox";
import { loadFAQs } from "~/utils/apis/loadFAQs";
import { loadProducts } from "~/utils/apis/loadProducts.server";
import { CheckIcon } from "lucide-react";

export async function loader({ request }: LoaderFunctionArgs) {
  const device_locale = getDeviceLocale(request);
  const faqs = loadFAQs(device_locale).then((r) => r.filter((faq) => faq.available_for_users));
  const products = await loadProducts(device_locale);

  return {
    faqs: await faqs,
    products,
  };
}

export default function ApothekeIndexPage() {
  const { faqs, products } = useLoaderData<typeof loader>();

  return (
    <div className="relative font-open-sans antialiased">
      <section id="start">
        <Hero />
      </section>
      <section id="benefits">
        <Benefits />
        <FeatureOverview />
      </section>
      <section id="services">
        <Services />
        <Onboarding2 />
      </section>
      <section id="products" className="pt-8">
        <TestOffersComponent
          products={products.slice(0, 6).map((p) => (
            <MedicalTestProfileBox
              key={p.medical_test_profile_guid}
              to={`products/${p.name}`}
              panel={{
                name: p.name,
                subline: p.subline,
                medical_tests: p.medical_tests,
              }}
            />
          ))}
        />
      </section>
      <section id="trainings">
        <Schulungen />
      </section>
      <section id="contact">
        <ContactUs />
      </section>
      <CustomersReview />
      {/* <Onboarding /> */}

      <section id="faq">
        <FAQComponent items={faqs} />
      </section>

      <PublicFooter />
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <div className="grid overflow-hidden pt-20 md:min-h-[60vh] md:pt-40 lg:min-h-screen">
      <div className="public-container grid items-center justify-between gap-4">
        <div className="flex flex-col items-center gap-3">
          <h1 className="mb-4 mt-16 animate-fade-scale-in text-wrap text-center text-2xl font-bold tracking-tighter sm:text-4xl md:mt-0 lg:text-7xl">
            {t("Partner der Apotheken für Kapillarblutuntersuchungen")}
          </h1>
          {/* <Link to="#vorteile" className="contents"> */}
          {/*   <MicrositeButton>{t("Kundenbetreuung Kontaktieren")}</MicrositeButton> */}
          {/* </Link> */}
        </div>
        <div className="flex md:px-28">
          <img src={landing_box} alt="" className="mb-3 mt-4 w-full animate-fade-scale-in rounded-3xl" />
        </div>
      </div>
    </div>
  );
}

function Benefits() {
  const { t } = useTranslation();

  return (
    <div className="mb-14 mt-10 grid grid-cols-2 gap-3 whitespace-pre-wrap bg-blue-550 p-4 pt-12 text-center text-white md:grid-cols-4">
      <div className="space-y-6 py-4">
        <img src={benefit1} alt="" className="mx-auto w-[72px] rounded-xl bg-white" />
        <h4 className="min-h-24 text-lg font-semibold">{t("Kein Vorabinvest \nnotwendig")}</h4>
      </div>
      <div className="space-y-6 py-4">
        <img src={no_test_kit} alt="" className="mx-auto w-[72px] rounded-xl bg-white" />
        <h4 className="min-h-24 text-lg font-semibold">{t("Keine TestKits - daher keine \nLagerhaltung/kein MHD")}</h4>
      </div>
      <div className="space-y-6 py-4">
        <img src={benefit3} alt="" className="mx-auto w-[72px] rounded-xl bg-white" />
        <h4 className="min-h-24 text-lg font-semibold">{t("Tests werden erst nach \nDurchführung berechnet")}</h4>
      </div>
      <div className="space-y-6 py-4">
        <img src={apothekeIcon} alt="" className="mx-auto w-[72px] rounded-xl bg-white" />
        <h4 className="min-h-30 text-lg font-semibold">{t("Nur in der Apotheke \nund nicht online verfügbar")}</h4>
      </div>
    </div>
  );
}

function FeatureOverview() {
  const { t } = useTranslation();

  return (
    <div className="public-container">
      <div className="text-center md:text-left">
        <h1 className="text-4.5xl font-bold leading-[50px] tracking-tighter sm:text-5.5xl md:leading-[60px]">
          {t("Uns gibt es nur in den stationären Apotheken")}
        </h1>
        <p className="mt-4 text-lg leading-6 text-gray-500 md:pr-[20%]">
          {t(
            "CaboCheck ist Partner der Apotheken und verbindet Sie mit Laboren, schafft die Infrastruktur und stellt alles zur Verfügung, was Sie benötigen, um schnell und einfach Blutuntersuchungen anzubieten.",
          )}
        </p>
        <p className="mt-4 text-lg leading-6 text-gray-500 md:pr-[20%]">
          {t(
            "Während andere Anbieter Sie lediglich als Erfüllungsgehilfe nutzen, wollen wir Ihnen die Möglichkeit bieten, sich als Gesundheitszentrum mit angeschlossener Labordiagnostik breiter aufzustellen und Ihr Beratungsspektrum auszubauen.",
          )}
        </p>
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-2">
        <div className="row-span-2 flex flex-col rounded-3xl bg-gray-100 px-8 pt-8">
          <h3 className="text-3xl font-bold">{t("Innovative Blutentnahme")}</h3>

          <p className="mt-4 flex-1 leading-6 text-gray-600">
            {t(
              "Unsere Labore verwenden Kapillarblut für die Untersuchung. Dafür nutzen wir unser patentiertes Vakuumgerät, das schnell und unkompliziert das Blut mittels Vakuum entnimmt. Dadurch wird nicht nur die Entnahme wesentlich einfacher, sondern auch die Qualität des Blutes enorm gesteigert, denn es wird kein Quetschblut produziert und somit eine Verunreinigung mit Gewebeflüssigkeit vermieden.",
            )}
          </p>
          <img src={benefitHero1} alt="" className="mt-16 w-full rounded-t-2xl object-cover" />
        </div>
        <div className="rounded-3xl bg-gray-100 px-8 pt-8">
          <h3 className="text-3xl font-bold">{t("Kein Vorabinvest")}</h3>

          <p className="mb-8 mt-4 leading-6 text-gray-600">
            {t(
              "Sie benötigen keine eigenen Laborgeräte. Unser Vakuumgerät, außerdem sämtliches Verbrauchs- und Werbematerial sowie unseren kontinuierlichen Support stellen wir Ihnen gegen eine geringe Monatspauschale zur Verfügung.",
            )}
          </p>
        </div>
        <div className="rounded-3xl bg-gray-100 px-8 pt-8">
          <h3 className="text-3xl font-bold">{t("Ihre Preise")}</h3>

          <p className="mt-4 leading-6 text-gray-600">
            {t(
              "Wir helfen Ihnen bei der Preisfindung mit einer Empfehlung, aber: Sie definieren Ihre Verkaufspreise selbst, denn Sie kennen Ihren Markt am besten. Wir rechnen die angefallenen Kosten erst zum Monatsende mit Ihnen ab.",
            )}
          </p>
          <img src={benefitHero2} alt="" className="mt-16 aspect-[1.4] w-full rounded-t-xl object-cover" />
        </div>
        <div className="flex flex-col overflow-hidden rounded-3xl bg-gray-100 px-8 pt-8">
          <h3 className="text-3xl font-bold">{t("Laborbefunde")}</h3>

          <p className="mt-4 flex-1 leading-6 text-gray-600">
            {t(
              "Nach Absprache mit Ihrem Kunden gibt es mehrere Möglichkeiten, die vom Laborarzt erstellten Testergebnisse per Email zu übermitteln:",
            )}
            <br />
            {t("- Ihr Kunde bekommt allein das Ergebnis")}
            <br />
            {t("- Sie und Ihr Kunde erhalten das Ergebnis")}
            <br />
            {t("- nur Sie erhalten das Ergebnis")}
          </p>
          <img src={benefitHero3} alt="" className="mt-4 aspect-[1.8] w-full rounded-t-xl object-cover" />
        </div>
        <div className="flex flex-col rounded-3xl bg-gray-100 px-8 pt-8">
          <h3 className="text-3xl font-bold">{t("Ihr Apothekerportal")}</h3>

          <p className="mt-4 flex-1 leading-6 text-gray-600">
            {t(
              "Mit unserer Software bekommen Sie alles, was Sie für einen reibungslosen Ablauf benötigen. Von der Testdurchführung über die Versandlogistik bis zu Inhalten für Ihre Social Media Aktivitäten erhalten Sie alles über unser intuitives Dashboard. Bieten Sie Ihrem Kunden auch direkt Terminbuchungen an, wir entwickeln uns ständig weiter- dank Ihrer Expertise.",
            )}
          </p>
          <img src={benefitHero4} alt="" className="mt-16 w-full rounded-t-xl object-cover" />
        </div>
      </div>
    </div>
  );
}

function Services() {
  const { t } = useTranslation();

  return (
    <div className="public-container mt-16">
      <div className="rounded-3xl bg-gray-100 px-8 pb-8 pt-8 lg:col-span-2">
        <h3 className="mb-8 text-3xl font-bold">{t("Alle unsere Services auf einen Blick")}</h3>
        <ul className="grid gap-2 text-gray-700 md:grid-rows-7 md:[grid-auto-flow:column]">
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Testungen mittels Kapillarblut inkl. patentiertem Vakuumgerät")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Test-Baukastensystem für Ihre individuell zusammengestellten Testkombinationen")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Umfangreiche Softwarelösung (Ihr CaboCheck-Portal)")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>
              {t("Zugriff zu allen Informationen rund um Verordnungen, Zertifikate, Rilibäk-konformer Prozess")}
            </span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Hotline für Ihre Fragen")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Prozess-Schulung Ihres Teams")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Zugriff auf Ärztlichen Leiter für Fachfragen")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Informationen zu  den Testparametern für Ihr Team im Portal")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Material für Proben und Versand")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Automatisierte Nachversorgung mit allen Materialien")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Analoge Werbemittel wie Flyer, Poster, individualisierte Preislisten")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Vorlagen für Ihre Social Media Werbung")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Ihre individuelle, personalisierte Homepage (Micropage)")}</span>
          </li>
          <li className="flex items-center">
            <CheckIcon className="mr-2 inline text-blue-500" size={16} />
            <span>{t("Terminbuchungssystem")}</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

function CustomersReview() {
  const { t } = useTranslation();
  const reviews = [
    {
      image: customer_1,
      name: "Daniela von Nida",
      role: "Alte Apotheke",
      review:
        "Für uns ist CaboCheck die perfekte Ergänzung zu unserer Empfehlung der Therapieoptimierung. Viele Dauermedikamente führen zu Mikronährstoffdefiziten, deren Untersuchung wir jetzt den Kunden anbieten können. Auch für unsere Darmberatung ist vor allem der Test auf Nahrungsmittelunverträglichkeiten ein echter Mehrwert! Wir freuen uns über die Möglichkeit, unser Beratungsfeld der Prävention mit CaboCheck weiter ausbauen zu können und somit noch mehr Kunden in ihrer Gesundheit zu unterstützen.",
    },
  ];

  return (
    <section className="public-container py-20">
      <h2 className="text-center text-4.5xl tracking-tighter lg:text-5xl">{t("Was unsere Kunden sagen")}</h2>
      <p className="mt-8 px-6 text-center leading-6 text-gray-600">
        {t("Am besten Fragen Sie einfach die Apotheker-Kollegen, die bereits CaboCheck nutzen.")}
      </p>
      <div className="mt-6 grid gap-8 lg:mt-16">
        {reviews.map((review, index) => (
          <div
            key={index}
            className="flex flex-col items-start gap-8 rounded-3xl bg-gray-50 p-10 sm:flex-row lg:gap-16 lg:p-20"
          >
            <img
              src={review.image}
              alt={review.name}
              className="aspect-square w-12 rounded-full object-cover sm:w-28"
            />
            <div>
              <p className="text-normal font-semibold sm:text-xl">{review.review}</p>
              <div className="mt-4 lg:mt-10">
                <h3 className="text-lg font-semibold text-gray-900 sm:text-xl">{review.name}</h3>
                <p className="text-gray-500 sm:text-xl">{review.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Onboarding() {
  const { t } = useTranslation();

  return (
    <div className="public-container">
      <div className="rounded-3xl bg-blue-550 p-8 text-white lg:space-y-16">
        <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-semibold">{t("Ihr Onboarding")}</h2>
            <p className="mt-4">
              {t(
                "Kontaktieren Sie uns und kommen Sie an Bord. Rechtzeitig zu Ihrem Start erhalten Sie unser Vakuumgerät sowie sämtliche Materialien.",
              )}
            </p>
            <p className="mt-6">
              {t("In unserer Software stellen wir Ihnen diverses Marketingmaterial für Ihre Werbung zur Verfügung.")}
            </p>
          </div>
          <div className="mt-4 flex flex-col overflow-hidden rounded-3xl bg-white px-4 pt-7 sm:px-8 sm:pt-14">
            <div className="flex-1" />
            <img src={onboarding_1} alt="" className="aspect-[1.5] w-full rounded-t-3xl object-cover" />
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-2 lg:gap-10">
          <div className="flex flex-col justify-center lg:order-1">
            <h2 className="mt-10 text-3xl font-semibold lg:mt-0">{t("Schulung")}</h2>
            <p className="mt-2 text-xl font-semibold">{t("Videos, Leitfäden und Liveschulungen")}</p>
            <p className="mt-1">
              {t(
                "Sie haben Zugriff auf Schulungsvideos und Leitfäden. Außerdem können Sie offene live Videoschulungen mit unserem Team nutzen. Wählen Sie einfach für Ihre Mannschaft  den besten individuellen Lernweg aus. Falls danach doch noch Fragen übrig bleiben, sind wir gern an Ihrer Seite.",
              )}
            </p>
          </div>

          <div className="mt-4 flex flex-col overflow-hidden rounded-3xl bg-white px-4 pt-7 sm:px-8 sm:pt-14">
            <div className="flex-1" />
            <img src={onboarding_2} alt="" className="aspect-[1.5] w-full rounded-t-3xl object-cover" />
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-2 lg:gap-10">
          <div className="flex flex-col justify-center">
            <h2 className="mt-10 text-3xl font-semibold">{t("Support")}</h2>
            <p className="mt-2">{t("Sie haben eine Frage, Schwierigkeiten oder Verbesserungsvorschläge?")}</p>
            <p className="mt-6">
              {t(
                "Dafür sind wir täglich von 9-15 Uhr für Sie da und helfen Ihnen am Telefon, per WhatsApp Chat und per Email.",
              )}
            </p>
          </div>

          <div className="mt-4 flex flex-col overflow-hidden rounded-3xl bg-white px-4 pt-7 sm:px-8 sm:pt-14">
            <div className="flex-1" />
            <img src={onboarding_3} alt="" className="aspect-[1.5] w-full rounded-t-3xl object-cover object-top" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Onboarding2() {
  const { t } = useTranslation();

  return (
    <div className="public-container mt-10">
      <div className="space-y-8 rounded-3xl bg-blue-550 p-8 text-white lg:space-y-16">
        <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="flex flex-col justify-center space-y-6">
            <h2 className="break-words text-[32px] font-bold leading-[3rem]">{t("Ihre personalisierte Micropage")}</h2>
            <ul className="list-cabo list-cabo-2 space-y-2 [&>li]:!bg-left-top">
              <li>
                <p>
                  <strong>{t("Sofort einsatzbereit ohne technischen Aufwand")}</strong>
                </p>
                {t("Die Micropage ist fertig für Sie entwickelt und sofort verfügbar – keine Programmierung notwendig")}
              </li>
              <li>
                <p>
                  <strong>{t("Professionelle Präsentation der Testangebote")}</strong>
                </p>
                {t("Ihre Kunden erhalten von Ihnen detaillierte Informationen zu den Tests")}
              </li>
              <li>
                <p>
                  <strong>{t("Nahtlose Integration in Ihre eigene Website")}</strong>
                </p>
                {t(
                  "Die Micropage kann eigenständig genutzt oder nahtlos in Ihre bestehende Website eingebunden werden",
                )}
              </li>

              <li>
                <p>
                  <strong>{t("Digitaler Kundenservice & Online-Terminvereinbarung")}</strong>
                </p>
                {t("Ihre Kunden können sich selbstständig informieren und Tests direkt buchen")}
              </li>

              <li>
                <p>
                  <strong>{t("Wettbewerbsvorteil & Erhöhung der Kundenbindung")}</strong>
                </p>
                {t(
                  "Sie heben sich mit Hilfe der Micropage digital von der Konkurrenz ab und bieten einen innovativen Mehrwert für Ihre Kunden.",
                )}
              </li>
            </ul>
          </div>
          <div className="mt-4 flex flex-col overflow-hidden rounded-3xl bg-white px-4 pt-7 sm:px-8 sm:pt-14">
            <div className="flex-1" />
            <img src={blue_1} alt="" className="w-full rounded-t-3xl object-cover" />
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-2 lg:gap-10">
          <div className="flex flex-col justify-center space-y-6 lg:order-1">
            <h2 className="mt-10 break-words text-[32px] font-bold leading-[3rem] lg:mt-0">
              {t("Ihr intelligentes Terminbuchungssystem")}
            </h2>
            <ul className="list-cabo list-cabo-2 space-y-2 [&>li]:!bg-left-top">
              <li>
                {t(
                  "Effiziente Terminübersicht – Behalten Sie jederzeit den Überblick über alle anstehenden Termine im Kalender.",
                )}
              </li>
              <li>
                {t(
                  "Automatische Kundendatenübernahme – für eine Blutentnahme sind alle relevanten Daten sofort verfügbar – keine gesonderte Eingabe mehr notwendig.",
                )}
              </li>
              <li>
                {t(
                  "Flexible Terminarten – Legen Sie individuelle Dienstleistungen mit frei definierbarer Dauer, Intervallen, Kapazitäten und Pausen an.",
                )}
              </li>
              <li>
                {t(
                  "Einfache Integration – Das Terminsystem ist direkt auf Ihrer Micropage verfügbar und kann per Codezeile in Ihre eigene Website eingebunden oder per Link in E-Mails genutzt werden",
                )}
              </li>
            </ul>
          </div>

          <div className="mt-4 flex flex-col overflow-hidden rounded-3xl bg-white px-4 pt-7 sm:px-8 sm:pt-14">
            <div className="flex-1" />
            <img src={blue_2} alt="" className="w-full rounded-t-3xl object-cover" />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="flex flex-col justify-center space-y-6">
            <h2 className="break-words text-[32px] font-bold leading-[3rem]">
              {t("Ihr eigenes Test-Baukastensystem („Build your own test“)")}
            </h2>
            <ul className="list-cabo list-cabo-2 space-y-2 [&>li]:!bg-left-top">
              <li>
                <p>
                  <strong>{t("Mehr Flexibilität & individuelle Beratung")}</strong>
                </p>
                {t(
                  "Sie können gezielt auf die individuellen Bedürfnisse Ihrer Kunden eingehen und Tests nach Symptomen oder gesundheitlichen Anliegen zusammenstellen.",
                )}
              </li>
              <li>
                <p>
                  <strong>{t("Höhere Kundenzufriedenheit & Kundenbindung")}</strong>
                </p>
                {t("Sie begeistern Ihre Kunden mit maßgeschneiderten Testkombinationen.")}
              </li>
              <li>
                <p>
                  <strong>{t("Umsatzsteigerung & Wettbewerbsstärke")}</strong>
                </p>
                {t("Heben Sie sich durch ein exklusives Angebot von der Konkurrenz ab.")}
              </li>
              <li>
                <p>
                  <strong>{t("Integration in die pharmazeutische Beratung")}</strong>
                </p>
                {t(
                  "Sie können Tests gezielt mit einer Medikamentenberatung kombinieren (z. B. Hormonstatus bei Antibabypille oder Eisenstatus bei Veganern).",
                )}
              </li>
            </ul>
          </div>
          <div className="mt-4 flex flex-col overflow-hidden rounded-3xl bg-white px-4 pt-7 sm:px-8 sm:pt-14">
            <div className="flex-1" />
            <img src={blue_3} alt="" className="w-full rounded-t-3xl object-cover" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Schulungen() {
  const { t } = useTranslation();

  useEffect(() => {
    // Load the Quinbook script inside the container
    const container = document.getElementById("quinbook-container");
    if (container) {
      const script = document.createElement("script");
      script.src = "https://cdn.quinbook.com/shop.js";
      script.setAttribute("data-shopid", "cc02d42b8939768b8a4f1e4d826faa79");
      script.async = true;
      container.appendChild(script);

      return () => {
        // Cleanup: remove the script when component unmounts
        if (container.contains(script)) {
          container.removeChild(script);
        }
      };
    }
  }, []);

  return (
    <div className="public-container mt-12">
      <h2 className="mb-8 text-center text-4.5xl tracking-tighter lg:text-5xl">{t("Schulungen")}</h2>
      <div id="quinbook-container" />
    </div>
  );
}

function ContactUs() {
  const { t } = useTranslation();

  return (
    <div className="public-container mt-12">
      <ContactBanner
        eyebrow={t("CaboLab kennenlernen")}
        title={t("Kontaktieren Sie uns noch heute")}
        description={t(
          "Registrieren Sie sich und wir melden uns bei Ihnen, um CaboLab, passende Labortests und den Start in Ihrer Apotheke gemeinsam zu planen.",
        )}
        to="/register/pharmacy/step2"
        buttonLabel={t("Kontakt aufnehmen")}
        imageSrc={ContactBannerImage}
        imageAlt="Apothekenteam im Beratungsgespräch"
      />
    </div>
  );
}
